import React, { useEffect } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Divider,
  Grid,
  Button,
  TextField,
  Checkbox,
  Switch,
  Tooltip,
  FormControlLabel
} from '@material-ui/core';
import { AutomationOptions } from 'components/Template';
import { SkeletonCardFields } from 'components/Loading';
import InfoIcon from '@material-ui/icons/Info';
import { Alert } from '@material-ui/lab';
import { CloseIcon, ListTags, SelectInput } from 'components';
import { useSelector } from 'react-redux';
import { getAttachmentTags } from 'store/attachmentTemplate/actions';
import { getEnvVar } from 'helpers';
import { disabledAttachmentTypes, ProductType } from 'constants.js';

const useStyles = makeStyles(theme => ({
  root: {},
  cardHeader: {},
  radioLabel: {
    marginLeft: 0,
    alignItems: 'baseline'
  },
  radioGroup: {
    marginLeft: '-1rem'
  },
  cardActions: {
    flexDirection: 'row-reverse'
  },
  inputGrid: {
    maxWidth: '40rem'
  },
  checkedIcon: {
    color: theme.palette.primary
  },
  error: {
    color: theme.palette.error.main
  },
  headerContainer: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between'
  },
  switchGroup: {
    display: 'flex'
  },
  switch: {
    marginLeft: '-0.7rem'
  },
  iconLabel: {
    marginLeft: '0.7rem'
  }
}));

const allTemplateOptions = [
  {
    label: 'PNG Image',
    value: 'PNG'
  },
  {
    label: 'PDF File',
    value: 'PDF'
  },
  {
    label: 'Word Doc',
    value: 'DOC'
  }
];

// Filter out disabled attachment types based on env config
const admDisabledAttachmentTypes = getEnvVar('ADM_DISABLED_ATTACHMENT_TYPES')?.split(',') || [...disabledAttachmentTypes];
const templateOptions = allTemplateOptions.filter(option => !admDisabledAttachmentTypes.includes(option.value));

const Details = props => {
  const {
    className,
    staticContext,
    template,
    next,
    showNav,
    isPopupDialog,
    useCampaignAutomation = true,
    ...rest
  } = props;

  const {
    goNext,
    thisTemplate,
    handleChange,
    onFocus,
    onBlur,
    getErrorMessage,
    shouldShowError,
    updateCurrentErrorValidationSegment,
    isLoading,
    toggleDefaultTemplate,
    togglePromotional,
    toggleSurveyResponseTemplate,
    modifyTags,
    isTemplateReadOnly,
    showWarningMessage,
    isDefaultTemplate,
    showDefaultCheckbox,
    isTenantDefaultTemplate,
    closeTemplate
  } = template;

  const tagList = useSelector(store => store.attachmentTemplate.tagList);

  useEffect(() => {
    updateCurrentErrorValidationSegment('details');
  }, [thisTemplate, updateCurrentErrorValidationSegment]);

  // Auto-select attachment type if only one option is available
  useEffect(() => {
    if (!isLoading && templateOptions.length === 1 && !thisTemplate?.type && handleChange) {
      handleChange({
        target: {
          name: 'type',
          value: templateOptions[0].value
        }
      });
    }
  }, [isLoading, thisTemplate?.type, handleChange]);

  const classes = useStyles();

  const areInitialSettingsDisabled = () => {
    const hasInitialData =
      isTemplateSaved() ||
      (!!thisTemplate.jsonContent &&
        !!thisTemplate.eventMetaData &&
        thisTemplate.eventMetaData.length > 0);
    const isUnsavedCopy = !!thisTemplate._isCopy && !isTemplateSaved();

    return hasInitialData && !isUnsavedCopy;
  };

  const isTemplateSaved = () =>
    !!(thisTemplate.uuid || thisTemplate.id);
  const isTemplatePropertiesLocked =
    !!(thisTemplate.uuid || thisTemplate.id) ||
    isTemplateReadOnly();
  const isCopyFlow = !!thisTemplate._isCopy || !!thisTemplate._isCopyTemplate;
  const areTemplateTogglesLockedInCopy = isCopyFlow;
  // Change implemented as part of Bug 1078741:
  // In ADM UI, SRT-enabled templates are restricted for Guest Insights templates (Communication & Attachment).
  // This logic can be reverted once Guest Insights supports Survey Response campaigns.
  const shouldShowSurveyResponseTemplateToggle =
    thisTemplate.productType !== ProductType.GUEST_INSIGHTS &&
    thisTemplate.productName !== 'GuestInsight';

  const SkeletonCardContent = props => {
    return (
      <CardContent>
        <Grid container spacing={0}>
          <SkeletonCardFields numberOfRows={2} />
        </Grid>
      </CardContent>
    );
  };

  const attachmentTypes = {
    list: templateOptions,
    keyId: 'value',
    valueId: 'value',
    displayValueId: 'label'
  };

  return (
    <div className={classes.root}>
      {isLoading && <SkeletonCardContent />}
      {!isLoading && (
        <Card {...rest} className={clsx(classes.root, className)}>
          <form autoComplete="off">
            {!isPopupDialog && (
              <React.Fragment>
                {showWarningMessage() && (
                  <Alert severity="warning">
                    Changes made to Global Default template will be reflected in
                    all tenants and properties.
                  </Alert>
                )}
                <div className={classes.headerContainer}>
                  <div style={{ display: 'flex', flexDirection: 'row' }}>
                    <CardHeader
                      subheader="* indicates a required field"
                      title="Attachment Details"
                      titleTypographyProps={{ variant: 'h1' }}
                    />
                    {isTemplateReadOnly() && (
                      <Tooltip
                        title={
                          'You do not have access to edit this template. ' +
                          (isTenantDefaultTemplate()
                            ? "Tenant Default templates can be edited only with 'ALL' property."
                            : 'Only admin can edit Global Default templates.')
                        }>
                        <InfoIcon size="small" style={{ marginTop: '26px' }} />
                      </Tooltip>
                    )}
                  </div>
                  <CloseIcon onClickClose={closeTemplate} />
                </div>
              </React.Fragment>
            )}
            <Divider />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item md={6} xs={12}>
                  <Grid item xs={12} className={classes.inputGrid}>
                    <TextField
                      disabled={isTemplateReadOnly()}
                      fullWidth
                      label="Attachment Name"
                      margin="dense"
                      name="name"
                      onChange={handleChange}
                      required
                      value={thisTemplate.name || ''}
                      variant="outlined"
                      onFocus={onFocus}
                      onBlur={onBlur}
                      error={shouldShowError('name')}
                      helperText={getErrorMessage('name')}
                    />
                  </Grid>

                  <AutomationOptions
                    isPopupDialog={isPopupDialog}
                    disabled={areInitialSettingsDisabled() || isCopyFlow}
                    useCampaignAutomation={useCampaignAutomation}
                    template={template}
                  />

                  <Grid item xs={12} className={classes.inputGrid}>
                    <SelectInput
                      disabled={isTemplateReadOnly()}
                      fullWidth
                      label="Attachment Type"
                      margin="dense"
                      name="type"
                      onFocus={onFocus}
                      onBlur={onBlur}
                      onChange={handleChange}
                      required
                      value={thisTemplate.type || ''}
                      error={shouldShowError('type')}
                      helperText={getErrorMessage('type')}
                      options={attachmentTypes}
                    />
                  </Grid>

                  {!isLoading && (
                    <Grid item xs={12} className={classes.inputGrid}>
                      <ListTags
                        tags={thisTemplate.tags || []}
                        isTemplateReadOnly={isTemplateReadOnly()}
                        action={getAttachmentTags}
                        modifyTags={modifyTags}
                        allTags={tagList}></ListTags>
                    </Grid>
                  )}

                  {showDefaultCheckbox() && (
                    <Grid
                      item
                      xs={12}
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        marginLeft: '0px'
                      }}>
                      <FormControlLabel
                        disabled={isDefaultTemplate()}
                        style={{ marginLeft: '0px' }}
                        label="Default Template"
                        control={
                          <Checkbox
                            checked={thisTemplate.defaultTemplate || false}
                            onChange={toggleDefaultTemplate}
                            color="primary"
                            value="true"
                          />
                        }
                      />
                      {!isDefaultTemplate() && (
                        <Tooltip title="If this checkbox is enabled, this template will be available for all tenants and properties.">
                          <InfoIcon size="small" style={{ marginTop: '8px' }} />
                        </Tooltip>
                      )}
                    </Grid>
                  )}
                  <Grid item xs={12} className={classes.switchGroup}>
                    <Grid item md={6} xs={12}>
                      <FormControlLabel
                        className={classes.iconLabel}
                        control={
                          <Switch
                            className={classes.switch}
                            checked={!!thisTemplate.promotional}
                            name="promotional"
                            onChange={togglePromotional}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            disabled={
                              isTemplatePropertiesLocked ||
                              areTemplateTogglesLockedInCopy
                            }
                            color="primary"
                            inputProps={{ 'aria-label': 'promotional toggle' }}
                          />
                        }
                        label="Promotional Campaign"
                      />
                    </Grid>
                    {shouldShowSurveyResponseTemplateToggle && (
                      <Grid item md={6} xs={12}>
                        <FormControlLabel
                          className={classes.iconLabel}
                          control={
                            <Switch
                              className={classes.switch}
                              checked={!!thisTemplate.surveyResponseTemplate}
                              name="surveyResponseTemplate"
                              onChange={toggleSurveyResponseTemplate}
                              onFocus={onFocus}
                              onBlur={onBlur}
                              disabled={
                                isTemplatePropertiesLocked ||
                                areTemplateTogglesLockedInCopy
                              }
                              color="primary"
                              inputProps={{ 'aria-label': 'survey response template toggle' }}
                            />
                          }
                          label="Survey Response Template"
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
            {(next || showNav) && (
              <>
                <Divider />
                <CardActions className={classes.cardActions}>
                  <Button
                    color="primary"
                    variant="contained"
                    disabled={isLoading}
                    link={next}
                    onClick={goNext}>
                    Next
                  </Button>
                </CardActions>
              </>
            )}
          </form>
        </Card>
      )}
    </div>
  );
};

export default Details;
